import { defineStore } from "pinia"

import { APISubscription } from "@api/subscription.api"

import { type APIError } from "@interfaces/APIError.interface"
import { type Subscription } from "@utils/interfaces/Subscription.interface"

interface APISubscriptionData {
  id: number
  price: number
  dateStart: string
  dateEnd: string
  isRecurrent: boolean
  nextSubscription: boolean
  loginHistory: string | null
  subscriptionType: Subscription["subscriptionType"] | null
  company: Subscription["company"] | null
}

export const useSubscriptionStore = defineStore("subscription", {
  state: () => {
    return {
      hasSubscriptions: false,
      subscriptions: [] as Subscription[]
    }
  },
  getters: {
    getSubscriptionsDatesOnce: state => {
      const monthsMap = new Map<
        string,
        {
          date: Date
          companies: Map<
            string,
            { company: Subscription["company"]; subscriptions: Subscription[] }
          >
        }
      >()

      state.subscriptions.forEach(subscription => {
        // Group by month and year (e.g., "2024-12")
        const monthKey = `${subscription.dateStart.getFullYear()}-${subscription.dateStart.getMonth()}`
        const companyKey = subscription.company?.name || "no-company"

        // Initialize month group if it doesn't exist
        if (!monthsMap.has(monthKey)) {
          monthsMap.set(monthKey, {
            date: subscription.dateStart,
            companies: new Map()
          })
        }

        const monthGroup = monthsMap.get(monthKey)
        if (monthGroup) {
          // Initialize company group within the month if it doesn't exist
          if (!monthGroup.companies.has(companyKey)) {
            monthGroup.companies.set(companyKey, {
              company: subscription.company,
              subscriptions: []
            })
          }

          // Add subscription to the company group
          const companyGroup = monthGroup.companies.get(companyKey)
          if (companyGroup) {
            companyGroup.subscriptions.push(subscription)
          }
        }
      })

      // Convert to array format and sort by date (most recent first)
      return Array.from(monthsMap.values())
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map(monthGroup => ({
          date: monthGroup.date,
          companies: Array.from(monthGroup.companies.values()).map(companyGroup => ({
            company: companyGroup.company,
            subscriptions: companyGroup.subscriptions.sort(
              (a, b) => b.dateStart.getTime() - a.dateStart.getTime()
            )
          }))
        }))
    }
  },
  actions: {
    async fetchSubscriptions(): Promise<Subscription[] | APIError> {
      const response = await APISubscription.getAllSubscriptions()
      if ("error" in response) return { error: response.error }

      if (!response.subscriptions.length) {
        this.hasSubscriptions = false
        this.subscriptions = []
        return []
      }

      // Convert date strings to Date objects
      const subscriptionsWithDates = response.subscriptions.map(
        (subscription: APISubscriptionData) => ({
          ...subscription,
          dateStart: new Date(subscription.dateStart),
          dateEnd: new Date(subscription.dateEnd)
        })
      )

      this.hasSubscriptions = subscriptionsWithDates.length > 0
      this.subscriptions = subscriptionsWithDates
      return subscriptionsWithDates
    },

    updateSubscriptionRenewal(subscriptionId: number, isRecurrent: boolean): void {
      const subscription = this.subscriptions.find(s => s.id === subscriptionId)
      if (subscription) {
        subscription.isRecurrent = isRecurrent
      }
    }
  }
})
